import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { FloorPlanViewerV2 } from '@/components/floor-plans/FloorPlanViewerV2';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { FloorPlan } from '@/types/floorPlanViewer';

/**
 * Floor Plan Viewer page that displays a floor plan with all its features
 */
export default function FloorPlanViewer() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [floorPlanId, setFloorPlanId] = useState('');
  const [floorPlan, setFloorPlan] = useState<(FloorPlan & { project_id: string }) | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check for floor plan ID in URL on mount and auto-load
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl) {
      setFloorPlanId(idFromUrl);
      // Auto-load immediately if ID provided in URL
      loadFloorPlanById(idFromUrl);
    }
  }, [searchParams]);

  const loadFloorPlanById = async (id: string) => {
    if (!id.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const supabase = await getAuthenticatedSupabaseClient();
      const { data, error } = await supabase
        .from('floor_plans')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setFloorPlan(data as FloorPlan & { project_id: string });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load floor plan');
    } finally {
      setIsLoading(false);
    }
  };

  const loadFloorPlan = async () => {
    await loadFloorPlanById(floorPlanId);
  };

  // Show loading state while fetching floor plan with ID from URL
  if (searchParams.get('id') && !floorPlan && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
          <p className="text-gray-500">Loading floor plan...</p>
        </div>
      </div>
    );
  }

  if (!floorPlan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Load Floor Plan</CardTitle>
            <CardDescription>
              Enter a floor plan ID to view
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="floorPlanId">Floor Plan ID</Label>
              <Input
                id="floorPlanId"
                placeholder="Enter a floor plan ID"
                value={floorPlanId}
                onChange={(e) => setFloorPlanId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadFloorPlan()}
              />
              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <Button onClick={loadFloorPlan} disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load Floor Plan'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleBack = () => {
    // Navigate to the project page if we have a project ID
    if (floorPlan?.project_id) {
      navigate(`/project/${floorPlan.project_id}`);
    } else {
      // Fallback to dashboard if no project ID
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 h-screen flex flex-col">
      <div className="flex-1">
        <FloorPlanViewerV2 
          floorPlan={floorPlan}
          onBack={handleBack}
        />
      </div>
    </div>
  );
}
